import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Modal from '../components/UI/Modal';

const ItemGroups = () => {
  const [groups, setGroups] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', status: 'active', product_ids: [] });
  const [searchTerm, setSearchTerm] = useState('');

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [gRes, pRes] = await Promise.all([
        api.get('/item-groups/list.php?with_products=true'),
        api.get('/products/list.php?page=1&limit=9999')
      ]);
      if (gRes.data.success) setGroups(gRes.data.data || []);
      if (pRes.data.success) setAllProducts(pRes.data.data.products || []);
    } catch (e) { setError('Failed to load data'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  useEffect(() => {
    if (selectedGroup) {
      const updated = groups.find(g => String(g.id) === String(selectedGroup.id));
      if (updated) setSelectedGroup(updated);
    }
  }, [groups]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', description: '', status: 'active', product_ids: [] });
    setShowForm(true);
  };

  const openEdit = (g) => {
    setEditing(g);
    setForm({
      name: g.name || '',
      description: g.description || '',
      status: g.status || 'active',
      product_ids: (g.products || []).map(p => p.id)
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, product_ids: form.product_ids.map(Number) };
      if (editing) {
        await api.post('/item-groups/update.php', { id: editing.id, ...payload });
      } else {
        await api.post('/item-groups/create.php', payload);
      }
      setShowForm(false);
      fetchAll();
    } catch (e) { setError('Failed to save group'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this group? Products will not be deleted.')) return;
    try {
      await api.post('/item-groups/delete.php', { id });
      if (selectedGroup && String(selectedGroup.id) === String(id)) setSelectedGroup(null);
      fetchAll();
    } catch (e) { setError('Failed to delete group'); }
  };

  const toggleProduct = (productId) => {
    setForm(prev => ({
      ...prev,
      product_ids: prev.product_ids.includes(productId)
        ? prev.product_ids.filter(id => id !== productId)
        : [...prev.product_ids, productId]
    }));
  };

  const selectAll = () => {
    setForm(prev => ({
      ...prev,
      product_ids: filteredProducts.map(p => p.id)
    }));
  };

  const deselectAll = () => {
    setForm(prev => ({ ...prev, product_ids: [] }));
  };

  const filteredProducts = allProducts.filter(p => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return p.name.toLowerCase().includes(term) || p.sku.toLowerCase().includes(term);
  });

  const productLabel = (p) => {
    const parent = allProducts.find(x => String(x.id) === String(p.parent_id));
    const parts = [];
    if (parent) parts.push(parent.name);
    parts.push(p.name);
    const variant = [p.size, p.color].filter(Boolean).join(', ');
    if (variant) parts.push(`(${variant})`);
    return parts.join(' > ');
  };

  if (loading) return <div className="text-center py-20 text-gray-600">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white">Item Groups</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Organize products into groups for collections, bundles, and offers</p>
        </div>
        <button onClick={openCreate} className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg">+ New Group</button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <h2 className="font-semibold text-gray-800 dark:text-white">Groups ({groups.length})</h2>
            </div>
            <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-[600px] overflow-y-auto">
              {groups.length === 0 ? (
                <p className="px-4 py-8 text-center text-gray-500">No groups yet.</p>
              ) : groups.map(g => (
                <div
                  key={g.id}
                  onClick={() => setSelectedGroup(g)}
                  className={`px-4 py-3 cursor-pointer transition-colors ${
                    selectedGroup && String(selectedGroup.id) === String(g.id)
                      ? 'bg-primary-50 dark:bg-primary-900/20 border-l-4 border-primary-500'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{g.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{g.description || 'No description'}</p>
                    </div>
                    <span className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 rounded-full">{g.product_count || 0}</span>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <button onClick={(e) => { e.stopPropagation(); openEdit(g); }} className="text-xs text-primary-600 hover:text-primary-900">Edit</button>
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(g.id); }} className="text-xs text-red-600 hover:text-red-900">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          {selectedGroup ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <div>
                  <h2 className="font-semibold text-gray-800 dark:text-white">{selectedGroup.name}</h2>
                  <p className="text-xs text-gray-500">{(selectedGroup.products || []).length} products assigned</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(selectedGroup)} className="px-3 py-1.5 text-sm bg-primary-600 hover:bg-primary-700 text-white rounded-lg">Manage Products</button>
                </div>
              </div>
              {(selectedGroup.products || []).length === 0 ? (
                <p className="px-4 py-12 text-center text-gray-500">No products in this group. Click "Manage Products" to add them.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Price</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {(selectedGroup.products || []).map(p => (
                        <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-4 py-2.5 text-sm text-gray-900">
                            {productLabel(p)}
                          </td>
                          <td className="px-4 py-2.5 text-sm font-mono text-gray-600">{p.sku}</td>
                          <td className="px-4 py-2.5 text-sm font-semibold text-right">₹{parseFloat(p.base_price || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md flex items-center justify-center" style={{ minHeight: '400px' }}>
              <div className="text-center text-gray-400">
                <p className="text-lg font-medium mb-1">Select a Group</p>
                <p className="text-sm">Choose a group from the left panel to view its products</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editing ? 'Edit Group' : 'Create Group'} size="lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Group Name *</label>
              <input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" required />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows="2" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm font-medium">Assign Products ({form.product_ids.length} selected)</label>
              <div className="flex gap-2">
                <button type="button" onClick={selectAll} className="px-3 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50">Select All</button>
                <button type="button" onClick={deselectAll} className="px-3 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50">Clear</button>
              </div>
            </div>
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-3 focus:ring-2 focus:ring-primary-500"
            />
            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-200">
              {filteredProducts.length === 0 ? (
                <p className="px-3 py-4 text-sm text-gray-500 text-center">No products found</p>
              ) : filteredProducts.map(p => (
                <label key={p.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.product_ids.includes(p.id)}
                    onChange={() => toggleProduct(p.id)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-800">{productLabel(p)}</span>
                  <span className="text-xs text-gray-500 ml-auto">{p.sku}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-gray-300 rounded-lg">Cancel</button>
            <button type="submit" className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg">{editing ? 'Update' : 'Create'} Group</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ItemGroups;
